/**
 * Individual Taxpayer Customer Management Types
 * Complete data models for individual taxpayers managed by CAs
 */

// ============================================================================
// ADDRESS TYPES
// ============================================================================

export interface Address {
  line1: string
  line2?: string
  city: string
  state: string
  pincode: string
  country: string
}

// ============================================================================
// FINANCIAL PROFILE TYPES
// ============================================================================

export type Occupation = 'salaried' | 'self_employed' | 'business' | 'retired' | 'other'
export type IncomeRange = '0-5L' | '5-10L' | '10-25L' | '25-50L' | '50L+'
export type Gender = 'male' | 'female' | 'other'
export type CustomerStatus = 'active' | 'inactive' | 'archived'

// ============================================================================
// ASSET INTERFACES
// ============================================================================

export interface Property {
  id: string
  type: 'residential' | 'commercial' | 'land' | 'other'
  address: Address
  estimatedValue: number
  isRented: boolean
  annualRentalIncome?: number
  purchaseDate?: string
  purchaseValue?: number
}

export interface Vehicle {
  id: string
  type: 'car' | 'two_wheeler' | 'commercial' | 'other'
  make: string
  model: string
  registrationNumber: string
  purchaseDate?: string
  purchaseValue?: number
}

export interface Investment {
  id: string
  type:
    | 'mutual_fund'
    | 'stocks'
    | 'fixed_deposit'
    | 'ppf'
    | 'epf'
    | 'nps'
    | 'insurance'
    | 'bonds'
    | 'other'
  name: string
  institution: string
  accountNumber?: string
  investedAmount: number
  currentValue?: number
  maturityDate?: string
}

export interface BankAccount {
  id: string
  bankName: string
  accountNumber: string
  ifscCode: string
  accountType: 'savings' | 'current' | 'salary'
  isPrimary: boolean
}

// ============================================================================
// ASSESSMENT (TAX FILING) INTERFACE
// ============================================================================

export type AssessmentStatus =
  | 'not_started'
  | 'documents_pending'
  | 'in_progress'
  | 'under_review'
  | 'filed'
  | 'processed'
  | 'refund_issued'
  | 'demand_raised'

export interface Assessment {
  id: string
  customerId: string
  financialYear: string // e.g., "2024-25"
  assessmentYear: string // e.g., "2025-26"
  itrType: 'ITR-1' | 'ITR-2' | 'ITR-3' | 'ITR-4' | 'ITR-5' | 'ITR-6' | 'ITR-7'
  status: AssessmentStatus
  dueDate: string
  filedDate?: string
  acknowledgeNumber?: string
  grossIncome?: number
  taxableIncome?: number
  taxPaid?: number
  refundAmount?: number
  demandAmount?: number
  notes?: string
  createdDate: string
  lastUpdated: string
}

// ============================================================================
// MAIN CUSTOMER INTERFACE
// ============================================================================

export interface IndividualCustomer {
  id: string
  /** Authenticated client account linked by customer access governance. */
  ownerUserId?: string | null

  // Basic Info
  fullName: string
  pan: string // Format: ABCDE1234F
  aadhaar?: string
  dateOfBirth: string // ISO date
  gender: Gender

  // Contact
  emailPrimary: string
  emailSecondary?: string
  mobilePrimary: string
  mobileSecondary?: string
  whatsappNumber?: string

  // Address
  residentialAddress: Address
  permanentAddress?: Address
  correspondenceAddress?: Address

  // Financial Profile
  occupation: Occupation
  employerName?: string
  annualIncomeRange: IncomeRange
  hasHouseProperty: boolean
  hasCapitalGains: boolean
  hasBusinessIncome: boolean
  previousYearTaxPaid?: number

  // Wealth (optional)
  properties?: Property[]
  vehicles?: Vehicle[]
  investments?: Investment[]
  bankAccounts?: BankAccount[]

  // Metadata & Assignment
  assignedCA: string // User ID (Primary Preparer / Staff)
  assignedCAName?: string // CA's full name for display
  reviewerUserId?: string
  reviewerName?: string
  researcherUserId?: string
  researcherName?: string
  branchId?: string
  teamId?: string
  portfolioId?: string
  status: CustomerStatus
  createdDate: string
  lastUpdated: string

  // Stats (computed)
  totalAssessments: number
  pendingAssessments: number
  overdueAssessments: number

  // Related data
  assessments?: Assessment[]
}

// ============================================================================
// INPUT TYPES FOR CREATE/UPDATE
// ============================================================================

export interface CreateCustomerInput {
  fullName: string
  pan: string
  aadhaar?: string
  dateOfBirth: string
  gender: Gender
  emailPrimary: string
  emailSecondary?: string
  mobilePrimary: string
  mobileSecondary?: string
  whatsappNumber?: string
  residentialAddress: Address
  permanentAddress?: Address
  correspondenceAddress?: Address
  occupation: Occupation
  employerName?: string
  annualIncomeRange: IncomeRange
  hasHouseProperty: boolean
  hasCapitalGains: boolean
  hasBusinessIncome: boolean
  previousYearTaxPaid?: number
}

export interface UpdateCustomerInput {
  fullName?: string
  pan?: string
  aadhaar?: string
  dateOfBirth?: string
  gender?: Gender
  emailPrimary?: string
  emailSecondary?: string
  mobilePrimary?: string
  mobileSecondary?: string
  whatsappNumber?: string
  residentialAddress?: Address
  permanentAddress?: Address
  correspondenceAddress?: Address
  occupation?: Occupation
  employerName?: string
  annualIncomeRange?: IncomeRange
  hasHouseProperty?: boolean
  hasCapitalGains?: boolean
  hasBusinessIncome?: boolean
  previousYearTaxPaid?: number
  status?: CustomerStatus
}

// ============================================================================
// FILTER TYPES
// ============================================================================

export interface CustomerFilters {
  status?: CustomerStatus | 'all'
  assignedCA?: string | 'all'
  occupation?: Occupation | 'all'
  incomeRange?: IncomeRange | 'all'
  hasPendingAssessments?: boolean
  hasPendingDocuments?: boolean
  state?: string | 'all'
}

export interface SavedFilterView {
  id: string
  name: string
  filters: CustomerFilters
  createdAt: string
}

// ============================================================================
// CA (Assigned User) INTERFACE
// ============================================================================

export interface AssignedCA {
  id: string
  firstName: string
  lastName: string
  email: string
  initials: string
  customerCount: number
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface CustomerListResponse {
  items: IndividualCustomer[]
  total: number
  page: number
  pageSize: number
}

// ============================================================================
// VALIDATION PATTERNS
// ============================================================================

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
export const AADHAAR_REGEX = /^[0-9]{12}$/
export const MOBILE_REGEX = /^[6-9][0-9]{9}$/
export const PINCODE_REGEX = /^[1-9][0-9]{5}$/
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// ============================================================================
// INDIAN STATES LIST
// ============================================================================

export const INDIAN_STATES = [
  { code: 'AN', name: 'Andaman and Nicobar Islands' },
  { code: 'AP', name: 'Andhra Pradesh' },
  { code: 'AR', name: 'Arunachal Pradesh' },
  { code: 'AS', name: 'Assam' },
  { code: 'BR', name: 'Bihar' },
  { code: 'CH', name: 'Chandigarh' },
  { code: 'CT', name: 'Chhattisgarh' },
  { code: 'DD', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: 'DL', name: 'Delhi' },
  { code: 'GA', name: 'Goa' },
  { code: 'GJ', name: 'Gujarat' },
  { code: 'HR', name: 'Haryana' },
  { code: 'HP', name: 'Himachal Pradesh' },
  { code: 'JK', name: 'Jammu and Kashmir' },
  { code: 'JH', name: 'Jharkhand' },
  { code: 'KA', name: 'Karnataka' },
  { code: 'KL', name: 'Kerala' },
  { code: 'LA', name: 'Ladakh' },
  { code: 'LD', name: 'Lakshadweep' },
  { code: 'MP', name: 'Madhya Pradesh' },
  { code: 'MH', name: 'Maharashtra' },
  { code: 'MN', name: 'Manipur' },
  { code: 'ML', name: 'Meghalaya' },
  { code: 'MZ', name: 'Mizoram' },
  { code: 'NL', name: 'Nagaland' },
  { code: 'OD', name: 'Odisha' },
  { code: 'PY', name: 'Puducherry' },
  { code: 'PB', name: 'Punjab' },
  { code: 'RJ', name: 'Rajasthan' },
  { code: 'SK', name: 'Sikkim' },
  { code: 'TN', name: 'Tamil Nadu' },
  { code: 'TG', name: 'Telangana' },
  { code: 'TR', name: 'Tripura' },
  { code: 'UP', name: 'Uttar Pradesh' },
  { code: 'UK', name: 'Uttarakhand' },
  { code: 'WB', name: 'West Bengal' },
] as const

export type IndianStateCode = (typeof INDIAN_STATES)[number]['code']

// ============================================================================
// CUSTOMER GOVERNANCE ASSIGNMENT TYPES
// ============================================================================

export interface CustomerAssignmentInput {
  assignedToUserId?: string
  assignedToName?: string
  reviewerUserId?: string
  reviewerName?: string
  researcherUserId?: string
  researcherName?: string
  branchId?: string
  teamId?: string
  portfolioId?: string
  portfolioOwnerUserId?: string
  reason: string
}

export interface CustomerAssignmentEvent {
  id: string
  tenantId: string
  customerType: 'individual' | 'business'
  customerId: number | string
  beforeAssignment: Record<string, any>
  afterAssignment: Record<string, any>
  reason: string
  changedBy: string
  createdAt: string
}
